import React, { useMemo, useState } from 'react';
import {
  Box,
  Modal,
  ModalBody,
  Badge,
  StepGroup,
  StepItem,
  Heading,
} from '@razorpay/blade/components';
import { BasicDetails } from './BasicDetails';
import { CompensationDetails } from './CompensationDetails';
import { StatutoryDetails } from './StatutoryDetails';
import { ReviewDetails } from './ReviewDetails';
import { DiscardModal } from './components/DiscardModal';
import { SaveDraftAndCloseModal } from './components/SaveDraftAndCloseModal';
import {
  SidebarContentWrapper,
  MainContentWrapper,
  MultiStepFormWrapper,
} from '../components/styles';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { steps } from './data';
import { getStepProgress, getStepIndicator } from './utils';
import { getPeoplePage } from 'utils/links';
import { useDraftEmployeeId } from './hooks/useDraftEmployeeId';
import useFeature from 'hooks/useFeature';
import { useDraftEmployeeData } from './hooks/useDraftEmployeeData';
import { CompensationBreakdown } from './CompensationBreakdown';

export const CreateEmployee = (): React.ReactElement => {
  const location = useLocation();
  const history = useHistory();
  const { draftEmployeeId } = useDraftEmployeeId();
  const { isEmployeeOnboardingEnabled } = useFeature('employeeOnboarding');
  const { data } = useDraftEmployeeData();

  const currentStepIndex = useMemo(() => {
    const stepId = steps.find((step) => location.pathname.includes(step.route))?.id;
    return stepId;
  }, [location.pathname]);

  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showSaveDraftAndCloseModal, setShowSaveDraftAndCloseModal] = useState(false);

  const moveToPeoplePage = () => {
    history.push(getPeoplePage(isEmployeeOnboardingEnabled));
  };

  if (
    (currentStepIndex === undefined || !draftEmployeeId) &&
    !location.pathname.includes(routePaths.employeeOnboarding.employee.basicDetails)
  ) {
    return <Redirect to={routePaths.employeeOnboarding.employee.basicDetails} />;
  }

  return (
    <Box>
      <Modal
        isOpen={true}
        onDismiss={() => {
          //do nothing
        }}
        size="full"
        accessibilityLabel="Add Employee">
        <ModalBody padding="spacing.0">
          <MultiStepFormWrapper>
            <SidebarContentWrapper>
              {Boolean(draftEmployeeId) && (
                <Badge color="primary" size="medium">
                  Saved in Drafts
                </Badge>
              )}
              <Heading
                marginTop="spacing.3"
                size="large"
                weight="semibold"
                color="surface.text.gray.normal">
                Add Employee
              </Heading>

              <Box marginTop="spacing.4">
                <StepGroup orientation="vertical" size="medium">
                  {steps.map((step) => (
                    <StepItem
                      key={step.id}
                      title={step.title}
                      stepProgress={getStepProgress(step.id, currentStepIndex ?? 0)}
                      marker={getStepIndicator(step.id, currentStepIndex ?? 0)}
                      isSelected={currentStepIndex === step.id}
                    />
                  ))}
                </StepGroup>
              </Box>
            </SidebarContentWrapper>

            <MainContentWrapper>
              <Switch>
                <Route
                  path={routePaths.employeeOnboarding.employee.basicDetails}
                  exact
                  render={() => (
                    <BasicDetails
                      setShowDiscardModal={setShowDiscardModal}
                      setShowSaveDraftAndCloseModal={setShowSaveDraftAndCloseModal}
                    />
                  )}
                />
                <Route
                  path={routePaths.employeeOnboarding.employee.compensation}
                  exact
                  render={() => (
                    <CompensationDetails
                      setShowDiscardModal={setShowDiscardModal}
                      setShowSaveDraftAndCloseModal={setShowSaveDraftAndCloseModal}
                    />
                  )}
                />
                <Route
                  path={routePaths.employeeOnboarding.employee.compensationBreakdown}
                  render={() => <CompensationBreakdown setShowDiscardModal={setShowDiscardModal} />}
                />
                <Route
                  path={routePaths.employeeOnboarding.employee.compliance}
                  exact
                  render={() => (
                    <StatutoryDetails
                      setShowDiscardModal={setShowDiscardModal}
                      setShowSaveDraftAndCloseModal={setShowSaveDraftAndCloseModal}
                    />
                  )}
                />
                <Route
                  path={routePaths.employeeOnboarding.employee.review}
                  exact
                  render={() => (
                    <ReviewDetails
                      setShowDiscardModal={setShowDiscardModal}
                      setShowSaveDraftAndCloseModal={setShowSaveDraftAndCloseModal}
                    />
                  )}
                />
              </Switch>
            </MainContentWrapper>
          </MultiStepFormWrapper>
        </ModalBody>
      </Modal>

      <DiscardModal
        isOpen={showDiscardModal}
        onCancel={() => setShowDiscardModal(false)}
        onContinue={moveToPeoplePage}
      />

      <SaveDraftAndCloseModal
        isOpen={showSaveDraftAndCloseModal}
        onCancel={() => setShowSaveDraftAndCloseModal(false)}
        onContinue={moveToPeoplePage}
      />
    </Box>
  );
};
